import { useState, useRef, useMemo } from 'react';
import { Upload, FileText, X, Check, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { deleteContract, uploadFile } from '../api/api';
import { useDispatch } from 'react-redux';
import { setDocument } from '../redux/slices/documentSlice';
import { getContracts } from '../api/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { addContractToSpace, getSpaces } from '../api/api';
import { handleApiError } from '../helpers/handleApiError';
export default function UploadView({ isDarkMode }) {
  const user = useSelector((state) => state.auth.user);
  const uid = user?.id;
  const [dragActive, setDragActive] = useState(false);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [showActionsPanel, setShowActionsPanel] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [spaceLoading, setSpaceLoading] = useState(false);
  const [showSpaceModal, setShowSpaceModal] = useState(false);
  const [addToSpaceLoading, setAddToSpaceLoading] = useState(false);
  const [selectedContractForSpace, setSelectedContractForSpace] =
    useState(null);
  const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3 MB
  const formatFileName = (name, maxLength = 38) => {
    if (name.length <= maxLength) return name;
    const extIndex = name.lastIndexOf('.');
    const ext = extIndex !== -1 ? name.slice(extIndex) : '';
    const base = extIndex !== -1 ? name.slice(0, extIndex) : name;
    const keep = Math.floor((maxLength - ext.length - 3) / 2);
    return `${base.slice(0, keep)}...${base.slice(-keep)}${ext}`;
  };

  const fetchSpaces = async () => {
    setSpaceLoading(true);
    try {
      const data = await getSpaces(uid);
      setSpaces(data?.spaces || []);
    } catch (err) {
      handleApiError(err, 'Failed to load spaces');
    } finally {
      setSpaceLoading(false);
    }
  };

  const handleFileUpload = async (files) => {
    for (const file of files) {
      // ⛔ Size Check
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds the 3 MB limit`);
        continue;
      }

      // ⛔ PDF Format Check
      if (
        file.type !== 'application/pdf' &&
        !file.name.toLowerCase().endsWith('.pdf')
      ) {
        toast.error(`${file.name} is not a PDF file`);
        continue;
      }

      const fileId = `upload-${Date.now()}-${file.name}`;

      // Track progress
      setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));

      // Optimistic UI update
      setUploadedFiles((prev) => [
        ...prev,
        { id: fileId, name: file.name, size: file.size, type: file.type },
      ]);

      try {
        // Actual upload
        await uploadFile(uid, file, (percent) => {
          setUploadProgress((prev) => ({
            ...prev,
            [fileId]: percent,
          }));
        });

        // Refresh contract list after success
        await fetchContracts();

        // Remove from temp UI list
        setUploadedFiles((prev) => prev.filter((f) => f.name !== file.name));

        toast.success(
          <span
            title={file.name}
            className="inline-block max-w-[260px] truncate align-middle"
          >
            {formatFileName(file.name)} uploaded successfully
          </span>
        );
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const fetchContracts = async () => {
    setContractsLoading(true);
    try {
      const data = await getContracts(uid);

      if (data?.detailed_contracts && Array.isArray(data.detailed_contracts)) {
        const normalized = data.detailed_contracts.map((file) => ({
          id: file.contract_id,
          contract_id: file.contract_id,
          name: file.filename,
          size: file.file_size || 0,
          type: file.filename.split('.').pop()?.toUpperCase() || 'PDF',
          uploadedAt: new Date(file.upload_date).getTime(),
          analysisCount: file.analysis_count ?? 0,
          has_space: file.has_space,
          space: file.space,
        }));

        setContracts(normalized);
      }
    } catch (err) {
      handleApiError(err, 'Failed to load recent documents');
    } finally {
      setContractsLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, []);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer.files;
    if (files.length > 1) {
      toast.error('Only one document is allowed at a time.');
      return;
    }
    handleFileUpload(files);
  };

  const handleSelectFile = (id) => {
    if (selectedFileIds.length >= 1) {
      toast.error('You can only select one file at a time.');
      return;
    }
    if (!selectedFileIds.includes(id)) {
      setSelectedFileIds([id]);
      const selectedFile = allRecentDocs.find((file) => file.id === id);
      if (selectedFile) {
        dispatch(setDocument({ fileName: selectedFile.name }));
      }
    }
  };

  const handleRemoveSelected = (id) => {
    setSelectedFileIds((prev) => prev.filter((fid) => fid !== id));
  };

  const allRecentDocs = useMemo(() => {
    return [...(contracts || []), ...(uploadedFiles || [])].filter(
      (file, index, self) =>
        index === self.findIndex((f) => f.name === file.name)
    );
  }, [contracts, uploadedFiles]);

  const selectedFiles = selectedFileIds
    .map((id) => allRecentDocs.find((file) => file.id === id))
    .filter(Boolean);

  const allSelectedUploaded =
    selectedFiles.length > 0 &&
    selectedFiles.every((file) => {
      const progress = uploadProgress[file.id];
      return progress === undefined ? true : progress === 100;
    });

  useEffect(() => {
    if (showActionsPanel && allSelectedUploaded && selectedFiles.length > 0) {
      const fileName = selectedFiles[0]?.name || 'Uploaded File';
      dispatch(setDocument({ fileName }));
      navigate('/app/document/action');
    }
  }, [
    showActionsPanel,
    allSelectedUploaded,
    selectedFiles,
    navigate,
    dispatch,
  ]);
  const handleDeleteContract = async (fileName) => {
    setDeleteLoading(true);
    try {
      await deleteContract(uid, fileName);
      toast.success(
        <span className="flex items-center gap-1 max-w-[300px]">
          <span
            title={fileName}
            className="inline-block max-w-[200px] truncate"
          >
            {formatFileName(fileName)}
          </span>
          <span className="shrink-0">deleted successfully</span>
        </span>
      );
      if (typeof fetchContracts === 'function') {
        fetchContracts();
      }
    } catch (err) {
      console.error(err);
      toast.error(`Failed to delete ${fileName}`);
    } finally {
      setDeleteLoading(false);
    }
  };
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  return (
    <div
      className="w-full px-4 py-6 sm:px-6 overflow-y-auto max-w-full"
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
    >
      <div className="max-w-6xl mx-auto w-full">
        <h2
          className={`text-xl font-semibold mb-1 
    ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}
  `}
        >
          Document Check
        </h2>

        <p
          className={`text-sm mb-3
    ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
  `}
        >
          Upload legal documents for AI-powered analysis and risk assessment.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={(e) => handleFileUpload(e.target.files)}
        />

        {/* Selected Files Panel */}
        {selectedFiles.length > 0 && (
          <div
            className={`rounded-lg px-4 py-6 mb-6 flex flex-col gap-6 border
      ${
        isDarkMode
          ? 'bg-[#1F2937] border-gray-700'
          : 'bg-[#F4F4FF] border-[#D3D3F8]'
      }
    `}
          >
            {/* File Cards */}
            <div className="flex flex-wrap sm:flex-nowrap gap-4">
              <AnimatePresence>
                {selectedFiles.map((file) => (
                  <motion.div
                    key={file.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -15 }}
                    transition={{ duration: 0.25 }}
                    className={`relative flex items-center w-full sm:w-[280px] border rounded-xl overflow-hidden
              ${
                isDarkMode
                  ? 'bg-[#1F2937] border-gray-600'
                  : 'bg-[#F4F4FF] border-brand'
              }
            `}
                  >
                    <button
                      onClick={() => handleRemoveSelected(file.id)}
                      className={`absolute top-3 right-3 transition
                ${
                  isDarkMode
                    ? 'text-gray-500 hover:text-red-400'
                    : 'text-gray-400 hover:text-red-500'
                }
              `}
                    >
                      <X className="w-4 h-4" />
                    </button>

                    <div
                      className={`flex items-center justify-center w-[80px] h-[80px] m-3 rounded-lg shrink-0
                ${isDarkMode ? 'bg-gray-700' : 'bg-white'}
              `}
                    >
                      <div
                        className={`flex items-center justify-center w-10 h-10 rounded-full
                  ${isDarkMode ? 'bg-gray-600' : 'bg-[#E8E8FC]'}
                `}
                      >
                        <FileText className="w-5 h-5 text-[#757EF0]" />
                      </div>
                    </div>

                    <div className="flex flex-col justify-center pr-4 flex-1 overflow-hidden">
                      <p
                        className={`text-sm font-medium leading-snug truncate
                  ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}
                `}
                      >
                        <span title={file.name}>
                          {formatFileName(file.name)}
                        </span>
                      </p>
                      <div
                        className={`flex justify-between text-xs mt-2
                  ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                `}
                      >
                        <span>{formatFileSize(file.size)}</span>
                        <span>{file.type || 'PDF'}</span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 mt-2 w-full">
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`text-sm px-12 py-2 rounded-md font-medium transition w-full sm:w-auto border
          ${
            isDarkMode
              ? 'border-brand text-brand hover:bg-[#2A2B3C]'
              : 'border-[#757EF0] text-[#757EF0] hover:bg-[#E6E7FB]'
          }
        `}
              >
                Add Files
              </button>
              <button
                disabled={!allSelectedUploaded}
                onClick={() => setShowActionsPanel(true)}
                className={`text-sm px-6 py-2 rounded-md font-medium transition w-full sm:w-auto 
          ${
            allSelectedUploaded
              ? 'bg-[#757EF0] text-white hover:bg-[#5c63d6]'
              : isDarkMode
              ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }
        `}
              >
                Check Documents
              </button>
            </div>
          </div>
        )}

        {/* Upload Drop Zone - only show when no selected files */}
        {selectedFiles.length === 0 && (
          <motion.div
            initial={false}
            animate={{
              scale: dragActive ? 1.02 : 1,
              backgroundColor: dragActive
                ? isDarkMode
                  ? '#2A2B3C'
                  : '#EEF0FF'
                : isDarkMode
                ? '#1F2937'
                : '#F4F4FF',
            }}
            transition={{ duration: 0.15 }}
            className={`border border-dashed rounded-lg text-center py-10 px-4 cursor-pointer 
      ${
        isDarkMode
          ? 'border-gray-700 text-gray-300'
          : 'border-gray-300 text-gray-700'
      }
    `}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex justify-center mb-4">
              <div
                className={`p-3 rounded-full 
          ${isDarkMode ? 'bg-gray-700' : 'bg-[#E1E3FC]'}
        `}
              >
                <Upload className="h-5 w-5 text-brand" />
              </div>
            </div>

            <p
              className={`text-sm font-semibold ${
                isDarkMode ? 'text-gray-200' : 'text-gray-700'
              }`}
            >
              Drop your documents here
            </p>
            <p
              className={`text-sm mt-1 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              or click to browse
            </p>

            <div className="mt-4">
              <div className="w-full sm:w-[280px] mx-auto py-2 text-sm font-medium bg-brand text-white rounded-md text-center hover:bg-[#6A73EB] transition cursor-pointer">
                Select Files
              </div>
            </div>
          </motion.div>
        )}

        {/* Recent Documents */}
        <h3
          className={`text-md font-medium mb-3 mt-3 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}
        >
          Recent Documents
        </h3>
        <div className=" w-full">
          <hr
            className={`mb-3 border-t ${
              isDarkMode ? 'border-gray-600' : 'border-gray-300'
            }`}
          />

          {contractsLoading ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-500">
              <Loader2 className="animate-spin h-6 w-6 mb-2" />
            </div>
          ) : allRecentDocs.length > 0 ? (
            <div className="space-y-3">
              <AnimatePresence>
                {allRecentDocs.map((file) => {
                  const progress = uploadProgress[file.id] || 100;
                  const isUploaded = progress === 100;
                  const isSelected = selectedFileIds.includes(file.id);
                  const formattedDate = new Date(
                    file.uploadedAt || file.lastModified || Date.now()
                  )
                    .toISOString()
                    .split('T')[0];

                  return (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-col gap-2 sm:flex-row sm:items-center justify-between py-3 px-3 rounded-md border border-transparent hover:border-gray-200 transition"
                    >
                      <div className="flex gap-3 items-start sm:items-center flex-1">
                        <div className="w-9 h-9 bg-[#F4F4FF] rounded-full flex items-center justify-center shrink-0">
                          <FileText className="w-5 h-5 text-[#757EF0]" />
                        </div>
                        <div className="overflow-hidden">
                          <p
                            className={`text-sm font-medium break-words ${
                              isDarkMode ? 'text-gray-100' : 'text-gray-900'
                            }`}
                          >
                            <span title={file.name}>
                              {formatFileName(file.name)}
                            </span>
                          </p>

                          <p className="text-xs text-gray-500">
                            Uploaded on {formattedDate}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3 justify-end items-center mt-1 sm:mt-0">
                        {isUploaded ? (
                          <span className="text-green-600 font-medium text-sm flex items-center">
                            <Check className="w-4 h-4 mr-1" /> Uploaded
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500 flex items-center">
                            <Loader2 className="animate-spin h-4 w-4 mr-1" />{' '}
                            Uploading
                          </span>
                        )}
                        {isUploaded && (
                          <button
                            onClick={() => handleSelectFile(file.id)}
                            disabled={isSelected}
                            className={`text-xs px-3 py-1 rounded-md border transition font-medium ${
                              isSelected
                                ? 'bg-[#757EF0] text-white cursor-not-allowed'
                                : 'text-[#757EF0] border-[#D3D3F8] hover:bg-brand hover:text-white'
                            }`}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </button>
                        )}
                        {isUploaded && (
                          <>
                            {file.has_space && file.space ? (
                              <motion.button
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                onClick={() =>
                                  navigate(
                                    `/app/spaces/${file.space.space_id}`,
                                    {
                                      state: { space: file.space },
                                    }
                                  )
                                }
                                className="flex items-center gap-1 px-3 py-1 rounded-md bg-indigo-50 text-indigo-600 text-xs font-medium hover:bg-indigo-100 transition"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="w-4 h-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                                  />
                                </svg>
                                {file.space.space_name}
                              </motion.button>
                            ) : (
                              <motion.button
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.3 }}
                                onClick={() => {
                                  setSelectedContractForSpace(file);
                                  fetchSpaces();
                                  setShowSpaceModal(true);
                                }}
                                className="text-xs px-2 py-1 rounded-md border border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                Add to Space
                              </motion.button>
                            )}
                          </>
                        )}

                        {isUploaded && (
                          <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => {
                              setFileToDelete(file.name);
                              setShowDeleteConfirm(true);
                            }}
                            className="text-xs px-3 py-1 rounded-md border border-red-300 text-red-500 hover:bg-red-50"
                          >
                            Delete
                          </motion.button>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No recent documents found.</p>
          )}
        </div>
      </div>
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            key="delete-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:p-8 p-5"
            >
              <h2 className="text-lg sm:text-xl font-bold text-brand mb-3 tracking-tight text-center">
                Confirm Delete
              </h2>
              <p className="text-sm text-gray-600 mb-6 text-center">
                Are you sure you want to delete{' '}
                <span
                  className="font-semibold inline-block max-w-full truncate"
                  title={fileToDelete}
                >
                  {formatFileName(fileToDelete)}?
                </span>
                <br></br> This action cannot be undone.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="w-full py-2 px-4 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setDeleteLoading(true);
                    try {
                      await handleDeleteContract(fileToDelete);
                      setShowDeleteConfirm(false);
                    } finally {
                      setDeleteLoading(false);
                    }
                  }}
                  disabled={deleteLoading}
                  className="w-full py-2 px-4 rounded-lg bg-red-500 hover:bg-red-600 text-white font-semibold shadow-sm transition-all flex items-center justify-center"
                >
                  {deleteLoading ? (
                    <Loader2 className="animate-spin w-4 h-4" />
                  ) : (
                    'Delete'
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showSpaceModal && (
          <motion.div
            key="space-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:p-8 p-5"
            >
              <h2 className="text-lg sm:text-xl font-bold text-brand mb-3 tracking-tight text-center">
                Add to Space
              </h2>

              {spaceLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="animate-spin w-6 h-6 text-brand" />
                </div>
              ) : spaces.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {spaces.map((space) => (
                    <button
                      key={space.id}
                      onClick={() =>
                        setSelectedContractForSpace((prev) => ({
                          ...prev,
                          space,
                        }))
                      }
                      className={`w-full text-left px-4 py-2 rounded-lg border text-sm font-medium transition ${
                        selectedContractForSpace?.space?.space_id ===
                        space.space_id
                          ? 'bg-indigo-100 border-indigo-400 text-indigo-700'
                          : 'border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {space.name}
                    </button>
                  ))}

                  {selectedContractForSpace?.space && (
                    <button
                      disabled={addToSpaceLoading}
                      onClick={async () => {
                        setAddToSpaceLoading(true);
                        try {
                          await addContractToSpace(
                            selectedContractForSpace.space.space_id,
                            selectedContractForSpace.contract_id,
                            uid
                          );

                          toast.success(
                            <span className="flex items-center gap-1 max-w-[320px]">
                              <span
                                title={selectedContractForSpace.name}
                                className="inline-block max-w-[140px] truncate"
                              >
                                {formatFileName(selectedContractForSpace.name)}
                              </span>

                              <span className="shrink-0">added to</span>

                              <span
                                title={selectedContractForSpace.space.name}
                                className="inline-block max-w-[140px] truncate"
                              >
                                {formatFileName(
                                  selectedContractForSpace.space.name
                                )}
                              </span>
                            </span>
                          );

                          await fetchContracts();
                          setShowSpaceModal(false);
                          setSelectedContractForSpace(null);
                        } catch (err) {
                          console.error(err);
                          toast.error('Failed to add to space');
                        } finally {
                          setAddToSpaceLoading(false);
                        }
                      }}
                      className={`w-full mt-3 py-2 rounded-lg font-semibold shadow-sm transition
    flex items-center justify-center gap-2
    ${
      addToSpaceLoading
        ? 'bg-indigo-400 cursor-not-allowed'
        : 'bg-brand hover:bg-brand'
    }
    text-white
  `}
                    >
                      {addToSpaceLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Adding…
                        </>
                      ) : (
                        'Add to Selected Space'
                      )}
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-center text-sm text-gray-500">
                    No spaces found. Create one first.
                  </p>

                  <div className="mt-3 flex justify-center">
                    <button
                      onClick={() => navigate('/app/spaces')}
                      className="px-4 py-2 rounded-md bg-brand text-white text-sm hover:bg-brand transition"
                    >
                      Go to Spaces
                    </button>
                  </div>
                </>
              )}

              <button
                disabled={addToSpaceLoading}
                onClick={() => setShowSpaceModal(false)}
                className={`mt-5 w-full py-2 rounded-lg font-medium transition
    ${
      addToSpaceLoading
        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
    }
  `}
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
